<template>
    <b-modal
        title-tag="h2"
        :title="editMode ? 'Edit Plan' : 'Enroll In Plan'"
        :lazy="true"
        :visible="isOpen"
        @close="close"
        @hidden="close"
        @shown="onOpen"
    >
        <form>
            <b-form-group label-cols="4" label="Plan:" label-for="plan">
                <b-input
                    v-if="editMode"
                    :value="existingRecord.template.name"
                    plaintext
                />
                <vue-select
                    v-else
                    label="name"
                    v-model="plan"
                    placeholder="Select plan (required)"
                    :options="planOptions"
                    @search="searchPlan"
                    @input="onSelectPlan"
                    v-validate="'required'"
                    name="plan"
                    :class="{
                        'border-danger': formError.includes('plan')
                    }"
                >
                    <template v-slot:no-options="{ search }">
                        <div class="no-opt">
                            <span v-if="search === ''">
                                Type to search
                            </span>
                            <span v-else-if="isLoadingPlanOptions">
                                Loading...
                            </span>
                            <span v-else>
                                No result
                            </span>
                        </div>
                    </template>
                </vue-select>
            </b-form-group>

            <b-form-group
                label-cols="4"
                label="Auto-Renew:"
                label-for="autorenew"
            >
                <b-select
                    v-model="autoRenew"
                    :options="[
                        {
                            text: 'Automatically renew',
                            value: '1'
                        },
                        {
                            text: 'Do not automatically renew',
                            value: '0'
                        }
                    ]"
                />
            </b-form-group>

            <b-form-group
                label-cols="4"
                label="Start Date:"
                label-for="startDate"
            >
                <b-input
                    v-if="editMode"
                    :value="_carbonDateDisplay(existingRecord.start_date)"
                    plaintext
                />
                <date-picker
                    v-else
                    :config="datePickerOptions"
                    v-model="startDate"
                    name="startDate"
                    placeholder="Date (required)"
                    :class="{
                        'border-danger': formError.includes('startDate')
                    }"
                />
            </b-form-group>

            <b-form-group
                label-cols="4"
                label="Payment Method:"
                label-for="payment_method_name"
                v-if="payer && payer.id"
            >
                <vet-select
                    v-model="paymentMethod"
                    id="payment_method_name"
                    name="payment_method_name"
                    module="payment-method"
                    :ccProcessor="paymentProcessor"
                    :payer="payer"
                    placeholder="Select payment method (optional)"
                    @options-loaded="setPrimaryPaymentMethod"
                    class="show-actions"
                />
            </b-form-group>

            <b-form-group
                v-if="isShowAutoBillOn"
                label-cols="4"
                label="Auto-Bill On:"
                label-for="autoBillOn"
            >
                <div
                    v-if="editMode && isChargeDayEditChanged"
                    class="auto-bill-on-inputs-edit"
                >
                    <span
                        v-if="loading"
                        v-html="loadingSelect"
                        class="m-0"
                    ></span>
                    <b-select
                        v-else
                        v-model="chargeDay"
                        :options="chargeDayOptions"
                    />
                    <span>of each month</span>
                    <autobill-info />
                </div>
                <div v-else class="auto-bill-on-inputs">
                    <span
                        v-if="loading"
                        v-html="loadingSelect"
                        class="m-0"
                    ></span>
                    <b-select
                        v-else
                        v-model="chargeDay"
                        :options="chargeDayOptions"
                    />
                    <span>starting in</span>
                    <span
                        v-if="loading"
                        v-html="loadingSelect"
                        class="m-0"
                    ></span>
                    <b-select
                        v-else
                        name="chargeMonth"
                        v-model="chargeMonth"
                        :options="chargeMonthOptions"
                        :class="{
                            'border-danger': formErrors.includes('chargeMonth')
                        }"
                    />
                    <autobill-info />
                </div>
            </b-form-group>

            <b-form-group
                label-cols="4"
                label="Enrolled By:"
                label-for="enrolledBy"
            >
                <vue-select
                    v-model="enrolledBy"
                    label="fullname"
                    :placeholder="staffPlaceholder"
                    :disabled="isLoadingListStaff"
                    :options="staffOptions"
                    :clearable="true"
                    class="show-actions"
                >
                    <template v-slot:open-indicator>
                        <span></span>
                    </template>
                </vue-select>
            </b-form-group>

            <b-form-group label-cols="4" label="Provider:" label-for="provider">
                <vue-select
                    name="provider"
                    v-model="provider"
                    label="providerName"
                    :placeholder="providerPlaceholder"
                    :disabled="isLoadingListProvider"
                    :options="providerOptions"
                    class="show-actions"
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('provider'),
                        rounded: true
                    }"
                >
                    <template v-slot:open-indicator>
                        <span></span>
                    </template>
                </vue-select>
            </b-form-group>

            <b-form-group label-cols="4" label="Notes:" label-for="notes">
                <b-textarea placeholder="Notes (optional)" v-model="notes" />
            </b-form-group>
        </form>

        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!isLoading"
                @click="close"
                class="float-left"
                variant="secondary"
            >
                Cancel
            </b-button>
            <div class="float-right">
                <img v-if="isLoading" src="assets/ajax-loader.gif" />
                <b-button
                    v-if="!isLoading"
                    @click="save"
                    class="float-left"
                    variant="primary"
                >
                    {{ editMode ? "Update" : "Enroll" }}
                </b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import vueSelect from "vue-select";
import { debounce, find, map, isEmpty, sortBy, isNil } from "lodash";
import moment from "moment";
import vetSelect from "@/module/commons/vet-select";
import PaymentDialogHelper from "@/module/payment/helper.js";
import AutobillInfo from "./wellness-modal-enroll-autobill-info";

export default {
    name: "modalEnrollInPlan",
    components: {
        vueSelect,
        vetSelect,
        AutobillInfo
    },
    mixins: [PaymentDialogHelper],
    props: {
        patientId: {
            type: String
        }
    },
    data() {
        return {
            formErrors: [],
            isLoading: false, // When loading for saving
            autoRenew: "1",
            originalChageDayEdit: null,
            isChargeDayEditChanged: false,
            datePickerOptions: {},
            formError: [],
            startDate: null,
            chargeDay: "1",
            chargeMonth: null,
            chargeMonthOptions: [],
            plan: null,
            planOptions: [],
            planTerm: null,
            isLoadingPlanOptions: false,
            notes: "",
            enrolledBy: null,
            provider: null,
            isLoadingListStaff: true,
            isLoadingListProvider: true,
            staffOptions: [],
            providerOptions: [],
            isShowPaymentMethod: false,
            paymentMethod: null,
            paymentProcessor: null,
            payer: {},
            clientId: null,
            currentUserId: null,
            defaultPaymentMethodId: null,
            loading: false, // When loading on open
            chargeDayOptions: [
                { text: "1st", value: "1" },
                { text: "2nd", value: "2" },
                { text: "3rd", value: "3" },
                { text: "4th", value: "4" },
                { text: "5th", value: "5" },
                { text: "6th", value: "6" },
                { text: "7th", value: "7" },
                { text: "8th", value: "8" },
                { text: "9th", value: "9" },
                { text: "10th", value: "10" },
                { text: "11th", value: "11" },
                { text: "12th", value: "12" },
                { text: "13th", value: "13" },
                { text: "14th", value: "14" },
                { text: "15th", value: "15" },
                { text: "16th", value: "16" },
                { text: "17th", value: "17" },
                { text: "18th", value: "18" },
                { text: "19th", value: "19" },
                { text: "20th", value: "20" },
                { text: "21st", value: "21" },
                { text: "22nd", value: "22" },
                { text: "23rd", value: "23" },
                { text: "24th", value: "24" },
                { text: "25th", value: "25" },
                { text: "26th", value: "26" },
                { text: "27th", value: "27" },
                { text: "28th", value: "28" }
            ]
        };
    },
    computed: {
        loadingSelect() {
            return `<select class="custom-select" >
                        <option selected>Loading...</option>
                    </select>`;
        },
        isShowAutoBillOn() {
            if (this.editMode && this.planTerm === "") {
                // for edit mode, we dont use 'plan' object.
                // instead, we use 'planTerm' directly
                return false;
            }

            if (this.plan && this.plan.term === 1) {
                return true;
            }

            if (this.plan && typeof this.plan.term == "undefined") {
                return true;
            }

            if (!this.plan) {
                return true;
            }

            return false;
        },
        editMode() {
            return (
                this.$store.state.patient.modalEnrollInPlan.action === "edit"
            );
        },
        isRenew() {
            return (
                this.$store.state.patient.modalEnrollInPlan.action === "add" &&
                !isEmpty(this.$store.state.patient.modalEnrollInPlan.record)
            );
        },
        existingRecord() {
            return this.$store.state.patient.modalEnrollInPlan.record;
        },
        staffPlaceholder() {
            return this.isLoadingListStaff
                ? "Loading..."
                : "Select staff member (optional)";
        },
        providerPlaceholder() {
            return this.isLoadingListProvider
                ? "Loading..."
                : "Select provider (required)";
        },
        isOpen() {
            return this.$store.state.patient.modalEnrollInPlan.isOpen;
        }
    },
    watch: {
        plan(value) {
            if (value) {
                this.formError = this.formError.filter(err => err !== "plan");
            }
        },
        startDate(value) {
            if (value) {
                this.formError = this.formError.filter(
                    err => err !== "startDate"
                );

                this.setAutoBillDay(value);
                this.setupChargeMonth();
            } else {
                this.formError.push("startDate");
            }
        },
        chargeDay(value) {
            this.setupChargeMonth();
        },
        chargeMonth(value) {
            this.formErrors = this.formErrors.filter(
                err => err !== "chargeMonth"
            );
        }
    },
    methods: {
        setPrimaryPaymentMethod(list = []) {
            let defaultPayment = list.find(
                payment => payment.id === this.defaultPaymentMethodId
            );

            if (defaultPayment) {
                this.paymentMethod = defaultPayment;

                // set once, then clear
                this.defaultPaymentMethodId = null;
            }
        },
        setAutoBillDay(value) {
            let momentObj = moment(value, this.getDateFormat());
            let day = momentObj.format("DD");
            let nDay = Number(day);
            if (nDay > 28) {
                nDay = 28;
            }
            if (isNaN(nDay)) {
                return;
            }
            this.chargeDay = nDay.toString();
        },
        async onOpen() {
            this.loading = true;
            // edit
            if (this.editMode) {
                const {
                    id,
                    is_auto_renew,
                    patient_id,
                    enrolled_by,
                    provider_id,
                    notes,
                    charge_day,
                    start_date,
                    default_provider
                } = this.existingRecord;

                this.originalChageDayEdit = charge_day;
                this.startDate = this._fromDateCarbon(start_date).toFormat(
                    this.getDateFormat()
                );
                this.autoRenew = is_auto_renew.toString();
                this.notes = notes;
                this.fetchStaffList(enrolled_by);
                await this.fetchProviderList(provider_id, default_provider);
                this.fetchDetailsEdit(id, patient_id);
            }
            // add
            else {
                this.datePickerOptions = {
                    format: this.getDateFormat(),
                    defaultDate: moment().tz(this._timeZone)
                };

                this.fetchStaffList();
                await this.fetchProviderList();
                this.fetchDetails(
                    this.isRenew ? this.existingRecord.template_id : null
                );

                this.setupChargeMonth();
                this.loading = false;
            }
        },
        close() {
            this.$store.commit("patient/closeModalEnrollInPlan");
        },
        async save() {
            let valid = await this.$validator.validateAll();

            if (
                this.isShowAutoBillOn &&
                !(this.editMode && this.isChargeDayEditChanged)
            ) {
                // This scenario is when the user is change the date of Auto-Bill On: and set it to previous date so the month is no longer as an option so the month will become empty
                const isOrignalChargeMonthInOpt = this.chargeMonthOptions
                    .map(opt => opt.value)
                    .includes(this.chargeMonth);
                if (!isOrignalChargeMonthInOpt) {
                    this.formErrors.push("chargeMonth");
                    return;
                }
            }
            if (!valid) {
                return false;
            }
            try {
                if (!this.editMode) {
                    if (!this.plan) {
                        this.formError.push("plan");
                        return;
                    }

                    if (!this.startDate || this.startDate === "") {
                        this.formError.push("startDate");
                        return;
                    }
                }

                let formData = new FormData();

                // edit
                if (this.editMode) {
                    formData.append("id", this.existingRecord.id);
                    formData.append(
                        "template_id",
                        this.existingRecord.template_id
                    );
                    formData.append(
                        "template",
                        this.existingRecord.template.name
                    );
                }
                // add
                else {
                    formData.append("template_id", this.plan.id);
                    formData.append("template", this.plan.name);
                    formData.append("charge_month", this.chargeMonth);
                    formData.append("date", this.startDate);
                }

                formData.append("pid", this.patientId);
                formData.append("cid", this.clientId);
                formData.append("note", this.notes);
                formData.append("is_auto_renew", this.autoRenew);
                formData.append(
                    "charge_month",
                    isNil(this.chargeMonth) ? "" : this.chargeMonth // If null or undefined, sent empty, else send the exact value including "0"
                );
                formData.append("charge_day", this.chargeDay || "");
                formData.append("provider_id", this.provider.id);

                // formData.append("continue", "");
                // formData.append("billing_after_expiry", "");
                // formData.append("is_add_payment_method", "0");

                if (this.enrolledBy) {
                    formData.append("enrolled_by", this.enrolledBy.id);
                }

                if (this.paymentMethod) {
                    let payment_method_id = this.paymentMethod.id;

                    if (typeof payment_method_id === "undefined") {
                        payment_method_id = this.paymentMethod
                            .payment_method_id;
                    }

                    formData.append("payment_method_id", payment_method_id);
                }

                this.isLoading = true;

                const res = await this.$http.post(
                    `${process.env.CI_APPS}patient/wellness_plan/save`,
                    formData
                );

                if (res.data.state === "1") {
                    throw new Error(res.data.message);
                }

                this.isLoading = false;
                this._message(res.data.message);
                this.close();
                this.$emit("reload");
            } catch (e) {
                this.isLoading = false;
                this._message(e.message, "danger");
            }
        },
        searchPlan(value, loading) {
            this.isLoadingPlanOptions = true;

            if (value === "") {
                this.isLoadingPlanOptions = false;
                this.planOptions = [];
                return;
            }

            this.fetchPlanOptions(value, this, loading);
        },
        async fetchDetailsEdit(planId, patientId) {
            const res = await this.$http.get(
                `${process.env.CI_APPS}patient/wellness_plan/edit`,
                {
                    params: {
                        patient_id: patientId,
                        plan_id: planId
                    }
                }
            );

            const {
                hide,
                cc_processor,
                owner_id,
                payment_method_id,
                selected_day,
                selected_month,
                term
            } = res.data;

            this.isShowPaymentMethod = hide !== "hide";
            this.paymentProcessor = cc_processor;
            this.payer.id = owner_id;
            this.clientId = owner_id;
            this.chargeDay = selected_day;
            this.chargeMonth = selected_month;
            this.planTerm = term;

            if (payment_method_id && payment_method_id !== "") {
                this.defaultPaymentMethodId = Number(payment_method_id);
            }

            this.loading = false;
        },
        async fetchDetails(templateId) {
            let params = {
                patient_id: this.patientId
            };

            if (templateId) {
                params.template_id = templateId;
            }

            const res = await this.$http.get(
                `${process.env.CI_APPS}patient/wellness_plan/add`,
                {
                    params
                }
            );

            const {
                hide,
                cc_processor,
                owner_id,
                payment_method_id,
                template_id,
                template_name,
                term,
                is_auto_renew,
                current_user_id
            } = res.data;

            this.isShowPaymentMethod = hide !== "hide";
            this.paymentProcessor = cc_processor;
            this.payer.id = owner_id;
            this.clientId = owner_id;
            this.autoRenew = is_auto_renew.toString();
            this.currentUserId = current_user_id;

            //set for renew plan
            if (this.isRenew) {
                this.plan = {
                    id: template_id,
                    name: template_name,
                    term
                };
            }

            // set default payment method
            if (payment_method_id && payment_method_id !== "") {
                this.defaultPaymentMethodId = Number(payment_method_id);
            }

            // set default provider
            let staff = this.providerOptions.find(
                rec => rec.id === this.currentUserId
            );
            let defaultClinicProvider = find(this.providerOptions, [
                "is_default_provider",
                true
            ]);

            if (defaultClinicProvider) {
                this.provider = defaultClinicProvider;
            } else if (staff) {
                this.provider = staff;
            } else {
                this.provider = this.providerOptions[0];
            }
        },
        fetchPlanOptions: debounce(async (value, self, loading) => {
            loading(true);

            // ci endpoint
            const res = await self.$http.get(
                process.env.APPS + "patient/wellness_plan/list_plan",
                {
                    params: {
                        search_key: value
                    }
                }
            );

            loading(false);
            self.planOptions = res.data || [];
            self.isLoadingPlanOptions = false;
        }, 300),
        onSelectPlan(value) {
            if (!value) {
                return;
            }

            this.plan = value;
        },
        async fetchStaffList(defaultStaffId) {
            try {
                this.isLoadingListStaff = true;
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}actor/staff`
                );

                const response = res.data.response;

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this.staffOptions = response.resources || [];
                    this.isLoadingListStaff = false;

                    // set default (for edit)
                    if (this.editMode && defaultStaffId) {
                        let staff = response.resources.find(
                            rec => rec.id === defaultStaffId
                        );

                        if (staff) {
                            this.enrolledBy = staff;
                        }
                    }
                } else {
                    throw new Error("Unable to get staff list");
                }
            } catch (e) {
                this.isLoadingListStaff = false;
                this.staffOptions = [];
            }
        },
        async fetchProviderList(defaultProviderId, clinicProvider) {
            try {
                this.isLoadingListProvider = true;
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}actor/staff/business/providers`
                );
                let providers = res.data.response.resource.data;

                this.providerOptions = map(providers, element => ({
                    id: element.actor.id,
                    providerName:
                        element.actor.firstname + " " + element.actor.lastname,
                    is_default_provider: element.is_default_provider
                }));

                // --- sort by provider name
                this.providerOptions = sortBy(this.providerOptions, [
                    "providerName"
                ]);
                this.isLoadingListProvider = false;

                // set default (for edit)
                if (this.editMode && defaultProviderId) {
                    let staff = this.providerOptions.find(
                        rec => rec.id === defaultProviderId
                    );

                    if (clinicProvider) {
                        clinicProvider = {
                            id: clinicProvider.id,
                            providerName:
                                clinicProvider.business.name + " Provider",
                            is_default_provider: true
                        };
                    }

                    if (staff || clinicProvider) {
                        this.provider = clinicProvider ? clinicProvider : staff;
                    }
                }
            } catch (e) {
                this.isLoadingListProvider = false;
                this.providerOptions = [];
            }
        },
        setupChargeMonth() {
            if (this.editMode) {
                this.isChargeDayEditChanged =
                    this.originalChageDayEdit == this.chargeDay;
                // return;
            }

            let momentStartDate = this.datePickerOptions.defaultDate;
            if (this.startDate) {
                momentStartDate = moment(this.startDate, this.getDateFormat());
            }

            if (!momentStartDate) {
                // momentStartDate undefined on closing modal
                return;
            }

            let momentCurrentDate = moment.tz(this._timeZone);
            let options = [];
            let nChargeDay = Number(this.chargeDay);
            let nStartDateDay = Number(momentStartDate.format("DD"));
            let nCurrentDateDay = Number(momentCurrentDate.format("DD"));
            // charge same as start date
            // apps only show one month if created mode
            if (nChargeDay == nStartDateDay && !this.editMode) {
                let value = 0;
                if (
                    (nChargeDay <= nCurrentDateDay &&
                        momentStartDate
                            .clone()
                            .startOf("day")
                            .isSameOrBefore(
                                momentCurrentDate.clone().startOf("day")
                            )) ||
                    momentStartDate.clone().format("MMMM") ==
                        momentCurrentDate.clone().format("MMMM")
                ) {
                    value = 1;
                }
                options = [
                    {
                        value: value,
                        text: momentCurrentDate
                            .clone()
                            .add(value, "month")
                            .format("MMMM")
                    }
                ];
            }
            // --
            else {
                let value = 0;

                // if (nChargeDay < nStartDateDay) { // from apps use this rule only

                if (
                    nChargeDay <= nCurrentDateDay ||
                    (nStartDateDay > nChargeDay &&
                        momentStartDate.clone().format("MMMM") ==
                            momentCurrentDate.clone().format("MMMM"))
                ) {
                    value = 1;
                }
                options = [
                    {
                        value: value,
                        text: momentCurrentDate
                            .clone()
                            .add(value, "month")
                            .format("MMMM")
                    },
                    {
                        value: value + 1,
                        text: momentCurrentDate
                            .clone()
                            .add(value + 1, "month")
                            .format("MMMM")
                    }
                ];
            }

            this.chargeMonthOptions = options;
            if (!this.editMode) {
                this.chargeMonth = options[0] ? options[0].value : null;
            }
        }
    }
};
</script>

<style scoped>
.auto-bill-on-inputs {
    display: grid;
    grid-template-columns: 1fr 80px 1fr 30px;
    align-items: center;
    column-gap: 5px;
}
.auto-bill-on-inputs span {
    text-align: center;
}
.auto-bill-on-inputs-edit {
    display: flex;
    align-items: center;
}
.auto-bill-on-inputs-edit select {
    width: 100px;
}
.auto-bill-on-inputs-edit span {
    margin: 0 10px;
}
.no-opt {
    padding: 0 5px;
}
</style>
